import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const KPICard = ({ icon, label, value, sub, color, trend, trendUp, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-card rounded-xl border border-border p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon name={icon} size={17} color="currentColor" />
      </div>
    </div>
    <div className="text-3xl font-bold text-foreground tracking-tight">{value}</div>
    {(trend || sub) && (
      <div className="flex items-center gap-2 mt-1.5">
        {trend && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
            trendUp ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
          }`}>
            <Icon name={trendUp ? 'TrendingUp' : 'TrendingDown'} size={10} color="currentColor" />
            {trend}
          </span>
        )}
        {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
      </div>
    )}
  </div>
);

const generateMonthlyData = () => {
  const months = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
  return months?.map((month, i) => ({
    month,
    tickets: Math.floor(40 + Math.random() * 60 + i * 5),
    resolved: Math.floor(35 + Math.random() * 50 + i * 4),
    revenue: Math.floor(8000 + Math.random() * 6000 + i * 800),
    customers: Math.floor(20 + Math.random() * 15 + i * 2),
  }));
};

const OrganizationDashboard = () => {
  const navigate = useNavigate();
  const { organizationId, orgMembership } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [monthlyData] = useState(generateMonthlyData);
  const [activeTab, setActiveTab] = useState('overview');

  const [ticketStats, setTicketStats] = useState({ open: 0, inProgress: 0, resolved: 0, total: 0, overdue: 0 });
  const [personnelStats, setPersonnelStats] = useState({ total: 0, active: 0, technicians: 0, managers: 0, sales: 0 });
  const [invoiceStats, setInvoiceStats] = useState({ total: 0, paid: 0, pending: 0, overdue: 0, totalAmount: 0, paidAmount: 0 });
  const [customerStats, setCustomerStats] = useState({ total: 0, active: 0 });
  const [invitationStats, setInvitationStats] = useState({ pending: 0, accepted: 0, total: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [topTechnicians, setTopTechnicians] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, [organizationId]);

  const fetchDashboardData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const [ticketsRes, personnelRes, invoicesRes, auditRes, customersRes, invitationsRes] = await Promise.all([
        supabase?.from('tickets')?.select('status, created_at, priority'),
        supabase?.from('personnel')?.select('id, is_active, role'),
        supabase?.from('invoices')?.select('status, total_amount'),
        supabase?.from('audit_logs')?.select('*')?.order('created_at', { ascending: false })?.limit(10),
        supabase?.from('customers')?.select('id, is_active'),
        supabase?.from('invitations')?.select('status')?.eq('organization_id', organizationId || ''),
      ]);

      if (ticketsRes?.data) {
        const t = ticketsRes?.data;
        const now = new Date();
        setTicketStats({
          open: t?.filter(x => x?.status === 'open')?.length,
          inProgress: t?.filter(x => x?.status === 'in_progress')?.length,
          resolved: t?.filter(x => x?.status === 'resolved' || x?.status === 'closed')?.length,
          total: t?.length,
          overdue: t?.filter(x => x?.status === 'open' && x?.priority === 'high')?.length,
        });
      }

      if (personnelRes?.data) {
        const p = personnelRes?.data;
        setPersonnelStats({
          total: p?.length,
          active: p?.filter(x => x?.is_active)?.length,
          technicians: p?.filter(x => x?.role === 'technician')?.length,
          managers: p?.filter(x => x?.role === 'manager')?.length,
          sales: p?.filter(x => x?.role === 'salesperson')?.length,
        });
        // Mock top technicians
        setTopTechnicians([
          { name: 'Alex Johnson', jobs: 47, rating: 4.9, role: 'Senior Technician' },
          { name: 'Maria Garcia', jobs: 41, rating: 4.8, role: 'HVAC Specialist' },
          { name: 'James Wilson', jobs: 38, rating: 4.7, role: 'Electrician' },
          { name: 'Sarah Chen', jobs: 35, rating: 4.9, role: 'Plumbing Expert' },
        ]);
      }

      if (invoicesRes?.data) {
        const inv = invoicesRes?.data;
        setInvoiceStats({
          total: inv?.length,
          paid: inv?.filter(i => i?.status === 'paid')?.length,
          pending: inv?.filter(i => i?.status === 'pending' || i?.status === 'sent')?.length,
          overdue: inv?.filter(i => i?.status === 'overdue')?.length,
          totalAmount: inv?.reduce((s, i) => s + (i?.total_amount || 0), 0),
          paidAmount: inv?.filter(i => i?.status === 'paid')?.reduce((s, i) => s + (i?.total_amount || 0), 0),
        });
      }

      if (customersRes?.data) {
        const c = customersRes?.data;
        setCustomerStats({ total: c?.length, active: c?.filter(x => x?.is_active !== false)?.length });
      }

      if (invitationsRes?.data) {
        const inv = invitationsRes?.data;
        setInvitationStats({
          pending: inv?.filter(i => i?.status === 'pending')?.length,
          accepted: inv?.filter(i => i?.status === 'accepted')?.length,
          total: inv?.length,
        });
      }

      if (auditRes?.data) setRecentActivity(auditRes?.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const ticketPieData = [
    { name: 'Open', value: ticketStats?.open || 12 },
    { name: 'In Progress', value: ticketStats?.inProgress || 8 },
    { name: 'Resolved', value: ticketStats?.resolved || 45 },
  ];

  const personnelBreakdown = [
    { name: 'Technicians', value: personnelStats?.technicians || 12, color: '#3b82f6' },
    { name: 'Managers', value: personnelStats?.managers || 4, color: '#8b5cf6' },
    { name: 'Sales', value: personnelStats?.sales || 6, color: '#f59e0b' },
  ];

  const performanceData = [
    { metric: 'Ticket Resolution', score: 87, target: 90 },
    { metric: 'Customer Satisfaction', score: 92, target: 85 },
    { metric: 'SLA Compliance', score: 78, target: 95 },
    { metric: 'First Response', score: 95, target: 90 },
    { metric: 'Invoice Collection', score: 83, target: 80 },
  ];

  const quickActions = [
    { label: 'New Ticket', icon: 'ClipboardList', path: '/ticket-management', color: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
    { label: 'Create Quote', icon: 'FileText', path: '/quote-management', color: 'bg-green-50 text-green-600 hover:bg-green-100' },
    { label: 'Add Personnel', icon: 'UserPlus', path: '/personnel-management', color: 'bg-purple-50 text-purple-600 hover:bg-purple-100' },
    { label: 'New Invoice', icon: 'DollarSign', path: '/invoice-management-center', color: 'bg-orange-50 text-orange-600 hover:bg-orange-100' },
    { label: 'Permissions', icon: 'ShieldCheck', path: '/permission-management', color: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100' },
    { label: 'Audit Log', icon: 'Activity', path: '/audit-activity-log', color: 'bg-red-50 text-red-600 hover:bg-red-100' },
  ];

  const collectionRate = invoiceStats?.totalAmount > 0
    ? Math.round((invoiceStats?.paidAmount / invoiceStats?.totalAmount) * 100)
    : 0;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'team', label: 'Team', icon: 'Users' },
    { id: 'finance', label: 'Finance', icon: 'DollarSign' },
    { id: 'activity', label: 'Activity', icon: 'Activity' },
  ];

  return (
    <>
      <Helmet><title>Organization Dashboard - FieldServi</title></Helmet>
      <Header />
      <main className="pt-16 min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Building2" size={16} color="#2563eb" />
                </div>
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Company Overview</span>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Organization Dashboard</h1>
              <div className="flex items-center gap-2 mt-1">
                <Icon name="Building2" size={14} color="#2563eb" />
                <span className="text-sm font-semibold text-blue-600">
                  {orgMembership?.organizations?.name || 'Your Organization'}
                </span>
              </div>
              <p className="text-muted-foreground text-sm mt-0.5">
                Company-wide performance metrics and operational overview.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchDashboardData(true)}
                disabled={refreshing}
                className="flex items-center gap-2 border border-border px-4 py-2 rounded-lg font-medium hover:bg-muted transition-smooth text-sm disabled:opacity-50"
              >
                <Icon name="RefreshCw" size={14} color="currentColor" className={refreshing ? 'animate-spin' : ''} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                onClick={() => navigate('/permission-management')}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-smooth text-sm"
              >
                <Icon name="ShieldCheck" size={14} color="currentColor" />
                Permissions
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-muted/50 rounded-xl p-1 mb-6 w-fit">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth ${
                  activeTab === tab?.id ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={14} color="currentColor" />
                {tab?.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* KPI Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard icon="ClipboardList" label="Total Tickets" value={ticketStats?.total || 65} sub={`${ticketStats?.open || 12} open`} color="bg-blue-100 text-blue-600" trend="+12%" trendUp={true} onClick={() => navigate('/ticket-management')} />
                    <KPICard icon="Users" label="Active Staff" value={personnelStats?.active || 18} sub={`of ${personnelStats?.total || 22} total`} color="bg-green-100 text-green-600" trend="+2" trendUp={true} onClick={() => navigate('/personnel-management')} />
                    <KPICard icon="DollarSign" label="Total Revenue" value={`$${(invoiceStats?.totalAmount || 48500)?.toLocaleString()}`} sub={`${collectionRate || 71}% collected`} color="bg-orange-100 text-orange-600" trend="+8.3%" trendUp={true} onClick={() => navigate('/invoice-management-center')} />
                    <KPICard icon="UserCheck" label="Customers" value={customerStats?.total || 84} sub={`${customerStats?.active || 72} active`} color="bg-purple-100 text-purple-600" trend="+5" trendUp={true} onClick={() => navigate('/customer-database')} />
                  </div>

                  {/* Secondary KPIs */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-card rounded-xl border border-border p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="AlertCircle" size={14} color="#ef4444" />
                        <span className="text-xs font-medium text-muted-foreground">Overdue Invoices</span>
                      </div>
                      <div className="text-2xl font-bold text-red-500">{invoiceStats?.overdue || 3}</div>
                      <p className="text-xs text-muted-foreground mt-0.5">require attention</p>
                    </div>
                    <div className="bg-card rounded-xl border border-border p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Mail" size={14} color="#8b5cf6" />
                        <span className="text-xs font-medium text-muted-foreground">Pending Invites</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-600">{invitationStats?.pending || 0}</div>
                      <p className="text-xs text-muted-foreground mt-0.5">{invitationStats?.accepted || 0} accepted</p>
                    </div>
                    <div className="bg-card rounded-xl border border-border p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="Clock" size={14} color="#f59e0b" />
                        <span className="text-xs font-medium text-muted-foreground">In Progress</span>
                      </div>
                      <div className="text-2xl font-bold text-amber-500">{ticketStats?.inProgress || 8}</div>
                      <p className="text-xs text-muted-foreground mt-0.5">active tickets</p>
                    </div>
                    <div className="bg-card rounded-xl border border-border p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name="CheckCircle" size={14} color="#10b981" />
                        <span className="text-xs font-medium text-muted-foreground">Resolution Rate</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {ticketStats?.total > 0 ? Math.round((ticketStats?.resolved / ticketStats?.total) * 100) : 87}%
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">this period</p>
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-card rounded-xl border border-border p-5">
                      <h3 className="font-heading font-semibold text-foreground mb-4">Monthly Activity</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={monthlyData}>
                          <defs>
                            <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Legend />
                          <Area type="monotone" dataKey="tickets" name="Tickets" stroke="#3b82f6" fill="url(#colorTickets)" strokeWidth={2} />
                          <Area type="monotone" dataKey="resolved" name="Resolved" stroke="#10b981" fill="url(#colorResolved)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="bg-card rounded-xl border border-border p-5">
                      <h3 className="font-heading font-semibold text-foreground mb-4">Ticket Status</h3>
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={ticketPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                            {ticketPieData?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-2 mt-2">
                        {ticketPieData?.map((item, i) => (
                          <div key={item?.name} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS?.[i] }} />
                              <span className="text-muted-foreground">{item?.name}</span>
                            </div>
                            <span className="font-semibold text-foreground">{item?.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Performance + Quick Actions */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-card rounded-xl border border-border p-5">
                      <h3 className="font-heading font-semibold text-foreground mb-4">Performance Metrics</h3>
                      <div className="space-y-4">
                        {performanceData?.map(item => (
                          <div key={item?.metric}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm text-foreground">{item?.metric}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Target: {item?.target}%</span>
                                <span className={`text-sm font-bold ${
                                  item?.score >= item?.target ? 'text-green-600' : 'text-orange-500'
                                }`}>{item?.score}%</span>
                              </div>
                            </div>
                            <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`absolute left-0 top-0 h-full rounded-full transition-all ${
                                  item?.score >= item?.target ? 'bg-green-500' : 'bg-orange-400'
                                }`}
                                style={{ width: `${item?.score}%` }}
                              />
                              <div className="absolute top-0 h-full w-0.5 bg-gray-400" style={{ left: `${item?.target}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-card rounded-xl border border-border p-5">
                      <h3 className="font-heading font-semibold text-foreground mb-4">Quick Actions</h3>
                      <div className="grid grid-cols-2 gap-2.5">
                        {quickActions?.map(action => (
                          <button
                            key={action?.label}
                            onClick={() => navigate(action?.path)}
                            className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-smooth ${action?.color}`}
                          >
                            <Icon name={action?.icon} size={18} color="currentColor" />
                            <span className="text-xs font-medium text-center leading-tight">{action?.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TEAM TAB */}
              {activeTab === 'team' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard icon="Users" label="Total Staff" value={personnelStats?.total || 22} sub="all roles" color="bg-blue-100 text-blue-600" trend={undefined} trendUp={undefined} onClick={undefined} />
                    <KPICard icon="Wrench" label="Technicians" value={personnelStats?.technicians || 12} sub="field staff" color="bg-green-100 text-green-600" trend={undefined} trendUp={undefined} onClick={undefined} />
                    <KPICard icon="Briefcase" label="Managers" value={personnelStats?.managers || 4} sub="supervisors" color="bg-purple-100 text-purple-600" trend={undefined} trendUp={undefined} onClick={undefined} />
                    <KPICard icon="TrendingUp" label="Sales Team" value={personnelStats?.sales || 6} sub="representatives" color="bg-orange-100 text-orange-600" trend={undefined} trendUp={undefined} onClick={undefined} />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Team Breakdown */}
                    <div className="bg-card rounded-xl border border-border p-5">
                      <h3 className="font-heading font-semibold text-foreground mb-4">Team Composition</h3>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={personnelBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                            {personnelBreakdown?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry?.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-2 mt-2">
                        {personnelBreakdown?.map(item => (
                          <div key={item?.name} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item?.color }} />
                              <span className="text-muted-foreground">{item?.name}</span>
                            </div>
                            <span className="font-semibold text-foreground">{item?.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top Performers */}
                    <div className="bg-card rounded-xl border border-border p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-semibold text-foreground">Top Performers</h3>
                        <button onClick={() => navigate('/personnel-management')} className="text-xs text-primary hover:underline">View All</button>
                      </div>
                      <div className="space-y-3">
                        {topTechnicians?.map((tech, i) => (
                          <div key={tech?.name} className="flex items-center gap-3">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              i === 0 ? 'bg-yellow-100 text-yellow-700' :
                              i === 1 ? 'bg-gray-100 text-gray-600' :
                              i === 2 ? 'bg-orange-100 text-orange-600' : 'bg-blue-50 text-blue-600'
                            }`}>{i + 1}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{tech?.name}</p>
                              <p className="text-xs text-muted-foreground">{tech?.role}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-semibold text-foreground">{tech?.jobs} jobs</p>
                              <div className="flex items-center gap-0.5 justify-end">
                                <Icon name="Star" size={10} color="#f59e0b" />
                                <span className="text-xs text-muted-foreground">{tech?.rating}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Invitations */}
                  <div className="bg-card rounded-xl border border-border p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-heading font-semibold text-foreground">Invitation Status</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">Track pending and accepted invitations</p>
                      </div>
                      <button
                        onClick={() => navigate('/personnel-management')}
                        className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-smooth"
                      >
                        <Icon name="UserPlus" size={12} color="currentColor" />
                        Invite Member
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-yellow-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-yellow-600">{invitationStats?.pending || 0}</div>
                        <p className="text-xs text-yellow-700 font-medium mt-1">Pending</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{invitationStats?.accepted || 0}</div>
                        <p className="text-xs text-green-700 font-medium mt-1">Accepted</p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">{invitationStats?.total || 0}</div>
                        <p className="text-xs text-blue-700 font-medium mt-1">Total Sent</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* FINANCE TAB */}
              {activeTab === 'finance' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard icon="DollarSign" label="Total Revenue" value={`${(invoiceStats?.totalAmount || 48500)?.toLocaleString()}`} sub="all invoices" color="bg-green-100 text-green-600" trend="+8.3%" trendUp={true} onClick={undefined} />
                    <KPICard icon="CheckCircle" label="Collected" value={`${(invoiceStats?.paidAmount || 34200)?.toLocaleString()}`} sub={`${invoiceStats?.paid || 34} invoices`} color="bg-blue-100 text-blue-600" trend={undefined} trendUp={undefined} onClick={undefined} />
                    <KPICard icon="Clock" label="Pending" value={invoiceStats?.pending || 12} sub="awaiting payment" color="bg-yellow-100 text-yellow-600" trend={undefined} trendUp={undefined} onClick={undefined} />
                    <KPICard icon="AlertCircle" label="Overdue" value={invoiceStats?.overdue || 3} sub="past due date" color="bg-red-100 text-red-600" trend={undefined} trendUp={undefined} onClick={undefined} />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-card rounded-xl border border-border p-5">
                      <h3 className="font-heading font-semibold text-foreground mb-4">Monthly Revenue</h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`} />
                          <Tooltip formatter={v => [`$${v?.toLocaleString()}`, 'Revenue']} />
                          <Bar dataKey="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="bg-card rounded-xl border border-border p-5">
                      <h3 className="font-heading font-semibold text-foreground mb-4">Collection Rate</h3>
                      <div className="flex items-center justify-center py-6">
                        <div className="relative w-36 h-36">
                          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                            <circle cx="60" cy="60" r="50" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                            <circle
                              cx="60" cy="60" r="50" fill="none"
                              stroke={collectionRate >= 80 ? '#10b981' : collectionRate >= 60 ? '#f59e0b' : '#ef4444'}
                              strokeWidth="12"
                              strokeDasharray={`${(collectionRate / 100) * 314} 314`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-bold text-foreground">{collectionRate || 71}%</span>
                            <span className="text-xs text-muted-foreground">collected</span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-green-600">{invoiceStats?.paid || 34}</p>
                          <p className="text-xs text-green-700">Paid</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-red-500">{invoiceStats?.overdue || 3}</p>
                          <p className="text-xs text-red-600">Overdue</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ACTIVITY TAB */}
              {activeTab === 'activity' && (
                <div className="space-y-6">
                  <div className="bg-card rounded-xl border border-border p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading font-semibold text-foreground">Recent Activity</h3>
                      <button onClick={() => navigate('/audit-activity-log')} className="text-xs text-primary hover:underline">View Full Log</button>
                    </div>
                    {recentActivity?.length === 0 ? (
                      <div className="text-center py-12">
                        <Icon name="Activity" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No recent activity</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {recentActivity?.map(log => (
                          <div key={log?.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/30 transition-smooth">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              log?.severity === 'error' ? 'bg-red-100' :
                              log?.severity === 'warning' ? 'bg-yellow-100' : 'bg-primary/10'
                            }`}>
                              <Icon
                                name={log?.severity === 'error' ? 'AlertCircle' : log?.severity === 'warning' ? 'AlertTriangle' : 'Activity'}
                                size={14}
                                color={log?.severity === 'error' ? '#ef4444' : log?.severity === 'warning' ? '#f59e0b' : 'var(--color-primary)'}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground">
                                <span className="font-medium">{log?.user_email?.split('@')?.[0] || 'System'}</span>
                                {' '}{log?.action?.toLowerCase()}{' '}
                                <span className="text-muted-foreground">{log?.resource_name || log?.resource_type}</span>
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {new Date(log.created_at)?.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                              log?.severity === 'error' ? 'bg-red-50 text-red-600' :
                              log?.severity === 'warning' ? 'bg-yellow-50 text-yellow-600' : 'bg-green-50 text-green-600'
                            }`}>{log?.severity || 'info'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default OrganizationDashboard;
